type RgbColor = {
  r: number;
  g: number;
  b: number;
};

export function getContrastColor(
  color: string | null | undefined,
  lightColor = '#ffffff',
  darkColor = '#000000'
): string {
  const rgb = parseColor(color);
  if (!rgb) {
    return lightColor;
  }

  const luminance = calculateRelativeLuminance(rgb);
  return luminance > 0.5 ? darkColor : lightColor;
}

export function resolveCssVariableValue(
  value: string,
  computedStyle: CSSStyleDeclaration
): string | null {
  if (!value) {
    return null;
  }

  const varMatch = value.match(/^var\((--[a-z0-9-]+)(?:,\s*([^)]+))?\)$/i);

  if (!varMatch) {
    return value;
  }

  const [, variableName, fallbackValue] = varMatch;
  const resolvedValue = computedStyle.getPropertyValue(variableName).trim();

  if (resolvedValue) {
    return resolveCssVariableValue(resolvedValue, computedStyle);
  }

  if (fallbackValue) {
    return resolveCssVariableValue(fallbackValue.trim(), computedStyle);
  }

  return null;
}

function parseColor(color: string | null | undefined): RgbColor | null {
  if (!color) {
    return null;
  }

  const normalizedColor = color.trim().toLowerCase();

  if (normalizedColor.startsWith('#')) {
    return hexToRgb(normalizedColor);
  }

  const rgbMatch = normalizedColor.match(
    /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*[0-9.]+\s*)?\)$/
  );

  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3])
    };
  }

  return null;
}

function hexToRgb(hexColor: string): RgbColor | null {
  const normalizedHex = hexColor.replace('#', '');

  if (normalizedHex.length === 3) {
    const [r, g, b] = normalizedHex.split('');

    return {
      r: parseInt(r + r, 16),
      g: parseInt(g + g, 16),
      b: parseInt(b + b, 16)
    };
  }

  if (normalizedHex.length === 6) {
    return {
      r: parseInt(normalizedHex.slice(0, 2), 16),
      g: parseInt(normalizedHex.slice(2, 4), 16),
      b: parseInt(normalizedHex.slice(4, 6), 16)
    };
  }

  return null;
}

function calculateRelativeLuminance({ r, g, b }: RgbColor): number {
  const [red, green, blue] = [r, g, b].map((channel: number) => {
    const normalizedChannel = channel / 255;
    return normalizedChannel <= 0.03928
      ? normalizedChannel / 12.92
      : Math.pow((normalizedChannel + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

